import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimesCircle, FaTimes } from "react-icons/fa";

// Global reference for imperative usage
let globalToastFn = null;

/**
 * Call this from anywhere (even outside React) to show a toast.
 * globalShowToast("Saved!", "success", "Done");
 */
export function globalShowToast(message, type, title) {
  if (globalToastFn) {
    globalToastFn(message, type, title);
  }
}

const TOAST_ICONS = {
  success: FaCheckCircle,
  error: FaTimesCircle,
  warning: FaExclamationTriangle,
  info: FaInfoCircle,
};

const TOAST_CLASSES = {
  success: "cm-toast--success",
  error: "cm-toast--error",
  warning: "cm-toast--warning",
  info: "cm-toast--info",
};

/**
 * Auto-detect toast type from message content
 */
function detectToastType(message) {
  if (!message || typeof message !== "string") return "info";
  const lower = message.toLowerCase();

  if (/error|failed|unable|could not|invalid|rejected|denied/.test(lower)) return "error";
  if (/warning|required|please |must |cannot|validation|missing|select at least/.test(lower)) return "warning";
  if (/success|saved|created|sent|approved|updated|deleted|completed|generated|added|removed|copied|uploaded|confirmed/.test(lower)) return "success";
  return "info";
}

let toastIdCounter = 0;

function SingleToast({ toast, onClose, index }) {
  const [isExiting, setIsExiting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onClose(toast.id), 300);
    }, 5000);

    return () => clearTimeout(timerRef.current);
  }, [toast.id, onClose]);

  const handleClose = () => {
    clearTimeout(timerRef.current);
    setIsExiting(true);
    setTimeout(() => onClose(toast.id), 300);
  };

  const Icon = TOAST_ICONS[toast.type] || TOAST_ICONS.info;
  const className = TOAST_CLASSES[toast.type] || TOAST_CLASSES.info;

  return (
    <div
      className={`cm-toast ${className} ${isExiting ? "cm-toast--exit" : ""}`}
      style={{
        position: "relative",
        top: "auto",
        right: "auto",
        marginBottom: "10px",
      }}
      onClick={handleClose}
      role="alert"
    >
      <div className="cm-toast__icon">
        <Icon />
      </div>
      <div className="cm-toast__content">
        {toast.title && <div className="cm-toast__title">{toast.title}</div>}
        <div className="cm-toast__message">{toast.message}</div>
      </div>
      <button className="cm-toast__close" onClick={handleClose} aria-label="Close">
        <FaTimes />
      </button>
    </div>
  );
}

export default function GlobalToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const originalAlertRef = useRef(null);

  const addToast = useCallback((message, type, title) => {
    const detectedType = type || detectToastType(String(message));
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev.slice(-4), { id, message: String(message), type: detectedType, title: title || null }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    // Store original alert
    originalAlertRef.current = window.alert;

    // Override window.alert
    window.alert = (message) => {
      addToast(message);
    };

    // Set global reference
    globalToastFn = addToast;

    return () => {
      // Restore original alert on unmount
      window.alert = originalAlertRef.current;
      globalToastFn = null;
    };
  }, [addToast]);

  return (
    <>
      {children}
      {createPortal(
        <div
          style={{
            position: "fixed",
            top: 24,
            right: 24,
            zIndex: 10001,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            pointerEvents: "none",
          }}
        >
          {toasts.map((toast, index) => (
            <div key={toast.id} style={{ pointerEvents: "auto" }}>
              <SingleToast toast={toast} onClose={removeToast} index={index} />
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
